import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import type { BillingPrefs } from '@/store/slices/userPreferences';
import { Panel, Field, Button, Select, Toggle, type SelectOption } from '../../../../components';
import { COUNTRIES, BUSINESS_TYPES, ACCOUNT_TYPES } from './billingOptions';
import { bankFieldsForCountry, isSepaCountry } from './bankRules';
import {
  CRYPTO_NETWORKS,
  assetsForNetwork,
  memoLabelForNetwork,
  memoRequiredForNetwork,
} from './cryptoRules';
import { STATE_COUNTRIES, type BillingErrors } from './billingValidation';
import type { PayoutMethod } from '../../useSettingsForms';
import {
  Form,
  Intro,
  Group,
  GroupTitle,
  Grid,
  FieldCell,
  FullCell,
  FieldError,
  DateLabel,
  DateInput,
  SaveError,
  Footer,
  Divider,
  MethodRow,
  MethodText,
  MethodTitle,
  MethodDesc,
  Notice,
} from './BillingSection.styles';

export type BillingSectionProps = {
  values: BillingPrefs;
  errors: BillingErrors;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onChange: (key: keyof BillingPrefs, value: string) => void;
  onToggleMethod: (method: PayoutMethod) => void;
  onSave: () => void;
};

/** Keys of `BillingPrefs` whose value is a string: the text / select / date fields
 *  these cells render. The boolean payout flags (`bankEnabled` / `cryptoEnabled`)
 *  are driven by `<Toggle>` instead, so they're excluded here. */
type BillingTextKey = {
  [K in keyof BillingPrefs]: BillingPrefs[K] extends string ? K : never;
}[keyof BillingPrefs];

/**
 * Billing Information: the payout + KYC details an owner submits so we can pay out
 * (withdraw) from their main wallet. The owner picks a payout method (a bank
 * account and/or a crypto wallet, one or both) on top of the always-required
 * identity/tax details. No payout endpoint exists yet, so the form persists to the
 * user-preferences API (the `billing` namespace) and is read back the same way.
 * Bank-account fields are conditional on the chosen bank country (IBAN + BIC for
 * SEPA; account number + local clearing code + SWIFT + bank address for
 * international wires, see `bankRules.ts`); crypto fields adapt to the chosen
 * network (asset list + conditional destination tag/memo, see `cryptoRules.ts`).
 * Validation lives in `billingValidation.ts`.
 */
export const BillingSection = ({
  values,
  errors,
  dirty,
  saving,
  saveError,
  justSaved,
  onChange,
  onToggleMethod,
  onSave,
}: BillingSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const currencies = useAppSelector((state) => state.currencies.currenciesList) ?? [];
  const currencyOptions: SelectOption[] = currencies.map((code) => ({ value: code, label: code }));

  // Closed option vocabularies translated at the render site so each label is
  // harvested AND re-evaluates on language change (the value stays the stable id).
  const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    individual: t('Individual (private person)'),
    business: t('Business / company'),
  };
  const BUSINESS_TYPE_LABELS: Record<string, string> = {
    sole_proprietor: t('Sole proprietor'),
    partnership: t('Partnership'),
    llc: t('Limited liability company (LLC / Ltd)'),
    corp: t('Corporation'),
    other: t('Other'),
  };
  const CRYPTO_NETWORK_LABELS: Record<string, string> = {
    bitcoin: t('Bitcoin'),
    ethereum: t('Ethereum (ERC-20)'),
    tron: t('Tron (TRC-20)'),
    bsc: t('BNB Smart Chain (BEP-20)'),
    solana: t('Solana'),
    polygon: t('Polygon'),
    xrp: t('XRP Ledger'),
    stellar: t('Stellar'),
  };
  const localize = (options: SelectOption[], labels: Record<string, string>): SelectOption[] =>
    options.map((option) => ({ ...option, label: labels[option.value] ?? option.label }));
  const accountTypeOptions = localize(ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS);
  const businessTypeOptions = localize(BUSINESS_TYPES, BUSINESS_TYPE_LABELS);
  const cryptoNetworkOptions = localize(CRYPTO_NETWORKS, CRYPTO_NETWORK_LABELS);

  // Bank-detail field labels + placeholders come from `bankRules.ts` (the closed,
  // country-specific field vocabulary). The English value is the stable key the
  // form + validator share; translate it for display here, where each literal is
  // harvested and re-evaluated on language change. Unmapped values fall through and
  // are still re-translated reactively by `<Field>`.
  const BANK_FIELD_LABELS: Record<string, string> = {
    IBAN: t('IBAN'),
    'SWIFT / BIC code': t('SWIFT / BIC code'),
    'Account number': t('Account number'),
    'Routing number (ABA)': t('Routing number (ABA)'),
    'Sort code': t('Sort code'),
    'IFSC code': t('IFSC code'),
    'BSB (Bank-State-Branch)': t('BSB (Bank-State-Branch)'),
    'Institution number': t('Institution number'),
    'Branch transit number': t('Branch transit number'),
    CLABE: t('CLABE'),
    'Bank code': t('Bank code'),
    'Branch code': t('Branch code'),
    'CNAPS code': t('CNAPS code'),
    'Bank address': t('Bank address'),
    'Intermediary bank SWIFT / BIC': t('Intermediary bank SWIFT / BIC'),
    'Intermediary bank account / reference': t('Intermediary bank account / reference'),
    'Bank code (código do banco)': t('Bank code (código do banco)'),
    'Agência (branch)': t('Agência (branch)'),
    'NUBAN account number': t('NUBAN account number'),
    'BIK (bank code)': t('BIK (bank code)'),
  };
  // Only the instructional placeholders are copy. The "e.g. …" sample identifiers
  // and the pure digit masks ("00-00-00", "000-000") are example/format-only data,
  // so they pass through untranslated (rendered as-is by `<Field>`).
  const BANK_FIELD_PLACEHOLDERS: Record<string, string> = {
    '9 digits': t('9 digits'),
    '3 digits': t('3 digits'),
    '5 digits': t('5 digits'),
    '18 digits': t('18 digits'),
    '12 digits': t('12 digits'),
    'Branch / head-office address': t('Branch / head-office address'),
  };
  const bankFieldLabel = (label: string): string => BANK_FIELD_LABELS[label] ?? label;
  const bankFieldPlaceholder = (placeholder: string): string => BANK_FIELD_PLACEHOLDERS[placeholder] ?? placeholder;

  // The crypto memo field is named per chain ("Destination tag" on XRP, otherwise
  // "Memo"); resolve the display label at render so both literals are harvested.
  // `memoLabelForNetwork` stays the single source of truth for the English key.
  const MEMO_LABELS: Record<string, string> = {
    'Destination tag': t('Destination tag'),
    Memo: t('Memo'),
  };
  const memoLabel = (network: string): string => {
    const key = memoLabelForNetwork(network);
    return MEMO_LABELS[key] ?? key;
  };

  const saveLabel = saving ? t('Saving…') : justSaved ? t('Saved ✓') : t('Save withdrawal details');

  // Both payout-method switches share one handler; the method comes from the
  // toggle's `data-id` (surfaced via `dataset.id`).
  const handleToggleMethod = (event: React.MouseEvent<HTMLElement>): void => {
    const id = event.currentTarget.dataset.id;
    if (id === 'bank' || id === 'crypto') onToggleMethod(id);
  };

  // On a failed submit, bring the first invalid field into view. Each errored cell
  // carries `data-billing-error`, so the first one in DOM order is the topmost
  // visible error. The scroll runs in an effect (not synchronously after onSave)
  // because the errors only render on the next commit. `onSave` revalidates and
  // produces a fresh `errors` object every click, so the effect always re-fires.
  const formRef = React.useRef<HTMLDivElement>(null);
  const scrollOnNextErrors = React.useRef(false);

  React.useEffect(() => {
    if (!scrollOnNextErrors.current) return;
    scrollOnNextErrors.current = false;
    formRef.current?.querySelector('[data-billing-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  const handleSubmit = (): void => {
    scrollOnNextErrors.current = true;
    onSave();
  };

  const fieldCell = (
    key: BillingTextKey,
    label: string,
    opts?: { placeholder?: string; numeric?: boolean; full?: boolean },
  ): React.ReactElement => {
    const Cell = opts?.full ? FullCell : FieldCell;
    return (
      <Cell key={key} data-billing-error={errors[key] ? '' : undefined}>
        <Field
          label={label}
          value={values[key]}
          placeholder={opts?.placeholder}
          numeric={opts?.numeric}
          onChange={(value) => onChange(key, value)}
        />
        {errors[key] ? <FieldError text={errors[key] as string} tag="span" /> : null}
      </Cell>
    );
  };

  const selectCell = (
    key: BillingTextKey,
    label: string,
    options: SelectOption[],
    placeholder: string,
  ): React.ReactElement => (
    <FieldCell key={key} data-billing-error={errors[key] ? '' : undefined}>
      <Select
        label={label}
        value={values[key]}
        options={options}
        placeholder={placeholder}
        error={errors[key]}
        onChange={(value) => onChange(key, value)}
      />
    </FieldCell>
  );

  const dateCell = (key: BillingTextKey, label: string): React.ReactElement => (
    <FieldCell key={key} data-billing-error={errors[key] ? '' : undefined}>
      <DateLabel text={label} tag="span" />
      <DateInput
        type="date"
        value={values[key]}
        $invalid={Boolean(errors[key])}
        aria-label={label}
        aria-invalid={errors[key] ? true : undefined}
        onChange={(event) => onChange(key, event.target.value)}
      />
      {errors[key] ? <FieldError text={errors[key] as string} tag="span" /> : null}
    </FieldCell>
  );

  const { bankCountry, accountType } = values;
  const isIndividual = accountType === 'individual';
  const isBusiness = accountType === 'business';

  // Each section is a self-contained block; we interleave a hairline Divider
  // between them below, so the conditional sections don't leave stray rules.
  const sections: Array<React.ReactElement | null> = [
    <Group key="account-type">
      <GroupTitle text="Account type" tag="span" />
      <Grid>
        {selectCell(
          'accountType',
          t('Are you withdrawing as an individual or a business?'),
          accountTypeOptions,
          t('Select account type'),
        )}
      </Grid>
    </Group>,

    isIndividual ? (
      <Group key="identity-individual">
        <GroupTitle text="Your details" tag="span" />
        <Grid>
          {fieldCell('accountHolderFirstName', t('First name'))}
          {fieldCell('accountHolderLastName', t('Last name'))}
          {dateCell('dateOfBirth', t('Date of birth'))}
        </Grid>
      </Group>
    ) : isBusiness ? (
      <Group key="identity-business">
        <GroupTitle text="Business details" tag="span" />
        <Grid>
          {fieldCell('businessLegalName', t('Business / legal entity name'), { full: true })}
          {selectCell('businessType', t('Business type'), businessTypeOptions, t('Select a business type'))}
          {fieldCell('beneficialOwnerFirstName', t('Beneficial owner first name'))}
          {fieldCell('beneficialOwnerLastName', t('Beneficial owner last name'))}
          {dateCell('dateOfBirth', t('Beneficial owner date of birth'))}
        </Grid>
      </Group>
    ) : (
      <Intro
        key="identity-hint"
        text="Choose individual or business above and we'll ask for the right details."
        tag="span"
      />
    ),

    accountType !== '' ? (
      <Group key="tax">
        <GroupTitle text="Tax & verification" tag="span" />
        <Grid>
          {fieldCell('taxId', isIndividual ? t('Tax ID / national ID') : t('Tax ID'))}
          {isBusiness ? fieldCell('vatNumber', t('VAT number (optional)')) : null}
        </Grid>
      </Group>
    ) : null,

    <Group key="payout-methods">
      <GroupTitle text="Payout method" tag="span" />
      <Intro text="Choose how you'd like to receive withdrawals from your wallet. You can set up one or both." tag="span" />
      <MethodRow $on={values.bankEnabled}>
        <MethodText>
          <MethodTitle text="Bank account" tag="span" />
          <MethodDesc text="Receive a SEPA (EU) or international bank transfer." tag="span" />
        </MethodText>
        <Toggle on={values.bankEnabled} id="bank" label={t('Pay out to a bank account')} onToggle={handleToggleMethod} />
      </MethodRow>
      <MethodRow $on={values.cryptoEnabled}>
        <MethodText>
          <MethodTitle text="Crypto wallet" tag="span" />
          <MethodDesc text="Receive payouts to a crypto wallet on a supported network." tag="span" />
        </MethodText>
        <Toggle on={values.cryptoEnabled} id="crypto" label={t('Pay out to a crypto wallet')} onToggle={handleToggleMethod} />
      </MethodRow>
      {errors.payoutMethod ? (
        <span data-billing-error="">
          <FieldError text={errors.payoutMethod} tag="span" />
        </span>
      ) : null}
    </Group>,

    values.bankEnabled ? (
      <Group key="bank">
        <GroupTitle text="Bank account" tag="span" />
        <Grid>
          {selectCell('bankCountry', t('Bank account country'), COUNTRIES, t('Select a country'))}
          {fieldCell('bankName', t('Bank name'))}
          {selectCell('currency', t('Withdrawal currency'), currencyOptions, t('Select a currency'))}
          {/* The account-identifier + international-wire fields are country-specific
              (IBAN, IFSC, BSB, routing, sort code, SWIFT, bank address, intermediary
              bank, etc.); see bankRules. IBAN / account number / bank address span
              the full width; the shorter clearing codes sit two-up. */}
          {bankCountry === ''
            ? null
            : bankFieldsForCountry(bankCountry).map((spec) => {
                const label = bankFieldLabel(spec.label);
                return fieldCell(spec.key, spec.required ? label : t('{{label}} (optional)', { label }), {
                  placeholder: spec.placeholder ? bankFieldPlaceholder(spec.placeholder) : undefined,
                  numeric: spec.numeric,
                  full: spec.key === 'iban' || spec.key === 'accountNumber' || spec.key === 'bankAddress',
                });
              })}
        </Grid>
        {bankCountry === '' ? (
          <Intro
            text="Choose your bank's country above and we'll ask for exactly the details that corridor needs (IBAN + BIC for SEPA; account number, SWIFT, local clearing code and bank address for international wires)."
            tag="span"
          />
        ) : isSepaCountry(bankCountry) ? (
          <Intro
            text="EU / SEPA transfer. We pay the account holder named above, in EUR by default."
            tag="span"
          />
        ) : (
          <Intro
            text="International wire. Funds go to the account holder named above, at the billing address below; an intermediary / correspondent bank is only needed on certain corridors."
            tag="span"
          />
        )}
      </Group>
    ) : null,

    values.cryptoEnabled ? (
      <Group key="crypto">
        <GroupTitle text="Crypto wallet" tag="span" />
        <Grid>
          {selectCell('cryptoNetwork', t('Network / chain'), cryptoNetworkOptions, t('Select a network'))}
          {selectCell(
            'cryptoAsset',
            t('Token / asset'),
            assetsForNetwork(values.cryptoNetwork),
            values.cryptoNetwork === '' ? t('Select a network first') : t('Select a token'),
          )}
          {fieldCell('walletAddress', t('Wallet address'), { full: true, placeholder: t('Paste the destination wallet address') })}
          {memoRequiredForNetwork(values.cryptoNetwork)
            ? fieldCell('cryptoMemo', memoLabel(values.cryptoNetwork))
            : null}
          {fieldCell('cryptoLabel', t('Label / nickname (optional)'))}
        </Grid>
        {memoRequiredForNetwork(values.cryptoNetwork) ? (
          <Notice
            text="This network requires a destination tag / memo. Sending without it can permanently lose your funds."
            tag="span"
          />
        ) : (
          <Intro
            text="Double-check the address and network before saving. Crypto payouts can't be reversed, and some exchange deposits also need a memo / destination tag."
            tag="span"
          />
        )}
      </Group>
    ) : null,

    <Group key="billing">
      <GroupTitle text="Billing address" tag="span" />
      <Grid>
        {fieldCell('addressLine1', t('Street address'), { full: true })}
        {fieldCell('addressLine2', t('Apartment / suite (optional)'), { full: true })}
        {fieldCell('city', t('City'))}
        {fieldCell(
          'stateProvince',
          STATE_COUNTRIES.has(values.country)
            ? t('State / province / region')
            : t('State / province / region (optional)'),
        )}
        {fieldCell('postalCode', t('Postal / ZIP code'))}
        {selectCell('country', t('Country'), COUNTRIES, t('Select a country'))}
      </Grid>
    </Group>,
  ];

  const visibleSections = sections.filter((section): section is React.ReactElement => section !== null);

  return (
    <Panel>
      <Form ref={formRef}>
        <Intro
          text="Add the details we need to pay out withdrawals from your main wallet: a bank account and/or a crypto wallet, plus the identity details we use for verification. We use these only for verification and payouts."
          tag="span"
        />

        {visibleSections.map((section, index) => (
          <React.Fragment key={section.key ?? index}>
            {index > 0 ? <Divider /> : null}
            {section}
          </React.Fragment>
        ))}

        {saveError ? <SaveError text={saveError} tag="span" /> : null}
        <Footer>
          <Button label={saveLabel} fullWidth disabled={!dirty || saving} onClick={handleSubmit} />
        </Footer>
      </Form>
    </Panel>
  );
};

export default BillingSection;
